/**
 * RichTextContent — renders a richtext token with embedded field resolution.
 *
 * Replaces the common pattern of:
 *   const val = useContentToken('path', { defaultValue: '...' });
 *   <div dangerouslySetInnerHTML={{ __html: val }} />
 *
 * With:
 *   <RichTextContent path="path" defaultValue="..." as="div" />
 *
 * Handles:
 * - Token resolution via useContentToken
 * - Field token substitution via resolveRichText
 * - EditableToken wrapping for edit-mode support
 */

import React from 'react';
import { useVibesites, useContentToken } from './context';
import { EditableToken } from './editable-token';
import { resolveRichText, stripEdgeEmptyParagraphs } from '../richtext-resolver';
import { verifyCrossLinks } from '../verify-cross-links';

// HTML tags that are spec'd to contain only "phrasing content" — they cannot
// legally hold a <p>, <div>, heading, etc. The browser silently auto-closes
// them when a block child appears, which silently breaks SSR hydration: the
// SSG'd <span dangerouslySetInnerHTML="<p>…">  loses its inner <p> to the
// surrounding parser, leaving an empty span in the DOM that no longer matches
// what React tries to hydrate (React #425).
const PHRASING_ONLY_TAGS = new Set(['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'a', 'label']);

/** Blank "spacer" paragraphs: empty, or containing only <br>(s)/whitespace.
 *  `collapseBlankParagraphs` removes these at render so they don't stack on top
 *  of the richtext paragraph margins. */
const BLANK_P_RE = /<p\b[^>]*>(?:\s|<br\s*\/?>)*<\/p>/gi;

/**
 * If the resolved richtext is a single wrapping `<p>…</p>` and the outer Tag
 * is phrasing-only (so the browser would refuse to nest the `<p>` inside it),
 * strip the wrapping `<p>` so we end up with valid, hydration-safe HTML. The
 * outer Tag already provides paragraph/heading semantics in this case.
 *
 * Edge empty-paragraph stripping is delegated to `stripEdgeEmptyParagraphs`
 * (shared with the editor) so the "what counts as empty" rules stay in one
 * place. Blank "spacer" paragraphs anywhere are removed afterward by
 * `collapseBlankParagraphs`, since margins now own paragraph spacing.
 *
 * Only strips when the inner content has no further block-level wrappers, so
 * multi-paragraph rich text with real content falls back to the unmodified
 * value (callers authoring multi-block content should use `as="div"`).
 */
function stripWrappingParagraph(html: string, tag: string): string {
  if (!PHRASING_ONLY_TAGS.has(tag)) return html;
  const cleaned = stripEdgeEmptyParagraphs(html);
  const match = cleaned.match(/^<p>([\s\S]*)<\/p>$/);
  if (!match) return cleaned || html;
  const inner = match[1];
  if (/<p[\s>]|<div[\s>]|<h[1-6][\s>]/i.test(inner)) return cleaned;
  return inner;
}

/**
 * Remove blank-line "spacer" paragraphs (`<p></p>`, `<p><br></p>`) at render.
 * Paragraph spacing is now owned by the richtext base CSS (margin-bottom on
 * <p>, injected by VibesitesProvider). Many sites have legacy spacer paragraphs
 * authored back when Tailwind zeroed <p> margins and a blank line was the only
 * way to add a gap; left in, they'd stack on top of the new margins and balloon
 * the spacing. Collapsing them here auto-heals that content at render WITHOUT
 * mutating what's stored — the real paragraphs space themselves via margin.
 * (Replaces the older `preserveBlankLines`, which did the opposite for the
 * pre-CSS world.)
 */
function collapseBlankParagraphs(html: string): string {
  return html.replace(BLANK_P_RE, '');
}

/** True when the HTML contains a block-level element that may not legally nest
 *  inside a phrasing-only tag (multi-paragraph blurbs, headings, lists, divs).
 *  `stripWrappingParagraph` only unwraps a *single* outer `<p>`, so anything
 *  with multiple blocks reaches here intact. */
const BLOCK_CONTENT_RE = /<(?:p|div|h[1-6]|ul|ol|li|blockquote)[\s>]/i;
function hasBlockContent(html: string): boolean {
  return BLOCK_CONTENT_RE.test(html);
}

export interface RichTextContentProps {
  /** Token path (e.g., 'hero.description') */
  path: string;
  /** Display label for the editor */
  label?: string;
  /** Token description for the editor */
  description?: string;
  /** Default property field for field-mapping mode */
  defaultField?: string;
  /** Default HTML template value */
  defaultValue?: string;
  /** HTML tag to render. Defaults to 'div'. */
  as?: 'div' | 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'li' | 'figcaption';
  /** Additional class name */
  className?: string;
  /** Additional inline styles */
  style?: React.CSSProperties;
}

export function RichTextContent({
  path,
  label,
  description,
  defaultField,
  defaultValue,
  as: Tag = 'div',
  className,
  style,
}: RichTextContentProps) {
  const { property, pagePaths } = useVibesites();
  const rawHtml = useContentToken(path, { defaultField, defaultValue });
  const resolvedHtmlRaw = rawHtml ? resolveRichText(rawHtml, property as Record<string, any>) : '';
  // Prune cross-page links to pages this site doesn't ship. Runs AFTER field
  // resolution (so hrefs are final) and BEFORE the block-level cleanup +
  // dangerouslySetInnerHTML. Fails open when pagePaths is unavailable.
  const verifiedHtml = verifyCrossLinks(resolvedHtmlRaw, pagePaths);
  const resolvedHtml = collapseBlankParagraphs(stripWrappingParagraph(verifiedHtml, Tag));

  // A phrasing-only tag (p, span, a, h*) can't legally contain block children.
  // When block content survives the single-<p> strip (e.g. a multi-paragraph
  // generated blurb, or manually-authored multi-paragraph richtext), promote
  // the wrapper to a <div> so we never emit invalid nested blocks that break
  // SSR hydration (React #425). Styling is class-based, so this is visually
  // transparent for callers that pass as="p".
  const effectiveTag = PHRASING_ONLY_TAGS.has(Tag) && hasBlockContent(resolvedHtml) ? 'div' : Tag;

  // EditableToken renders <Tag>{children}</Tag>, so we pass resolved HTML
  // as a span with dangerouslySetInnerHTML to avoid double-wrapping.
  return (
    <EditableToken
      path={path}
      type="richtext"
      label={label}
      description={description}
      defaultField={defaultField}
      defaultValue={defaultValue}
      as={effectiveTag as any}
      className={className}
      style={style}
    >
      <span data-vibesite-richtext="" dangerouslySetInnerHTML={{ __html: resolvedHtml }} style={{ display: 'contents' }} />
    </EditableToken>
  );
}

export default RichTextContent;
